/**
 * WizardJourney — the upload wizard's four steps drawn as a road, with the Kombi at the current one.
 *
 * WHAT THIS REPLACES, AND WHY StepPills IS STILL HERE. `StepPills` is a VERBATIM port of the design's
 * own component and it stays that way: this file does not edit it, and `WIZARD_STEPS` — the design's
 * four-step copy — is still imported from it, so the wording has exactly one source of truth. The six
 * `<StepPills activeStep={n} />` call sites now say `<WizardJourney activeStep={n} />` instead, which
 * makes the swap reversible in six one-line edits if the road turns out to be the wrong call.
 *
 * WHY THE ROAD RATHER THAN THE PILLS. The wizard already ends by telling the builder their tool has
 * "found its seat on the Kombi" (P5's frame copy). Four pills say "you are on step 3 of 4"; a road says
 * the four screens are one journey, which is the thing the copy has always claimed and the pills never
 * showed. The pills' two-axis active treatment (tone AND label colour) is preserved in spirit — the
 * current stop is the only one that changes both colour and weight.
 *
 * activeStep IS 1-BASED, matching StepPills and the numbers the design draws, so the call sites did not
 * have to change their argument. KombiJourney is 0-based, hence the -1.
 *
 * STEP 4 IS ARRIVAL. The fourth step is P5, reached by the upload SUCCEEDING rather than by navigating,
 * so being on it means the journey is genuinely over — that is why it passes `complete` and the van
 * parks beside the sign instead of idling at the last milestone.
 *
 * HEIGHT. Deliberately smaller than the component's default: `--kj-van-h` is 30px here against 44,
 * because this sits in a fixed 640px modal whose body is what pays for every pixel the header takes.
 * At 30 the whole indicator is close to the pills' own 32px row plus a line of labels.
 */
import { KombiJourney } from '../../../design/KombiJourney';
import { WIZARD_STEPS } from './StepPills';

/** The number of drawn steps. Step 4 ("Publish your tool") is the arrival. */
const FINAL_STEP = WIZARD_STEPS.length;

export interface WizardJourneyProps {
  /** 1-based, matching StepPills and the numbers the design draws. */
  activeStep: number;
  className?: string;
}

export function WizardJourney({ activeStep, className = '' }: WizardJourneyProps) {
  return (
    <KombiJourney
      stops={WIZARD_STEPS}
      current={activeStep - 1}
      complete={activeStep >= FINAL_STEP}
      label="Upload progress"
      className={className}
      vanHeight={30}
    />
  );
}
